import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Briefcase, MessageCircle, Phone, Send, X } from "lucide-react";
import { isEmail, submitForm } from "../lib/submit";
import { SITE } from "../data/site";

export default function ChatWidget() {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({ name: "", email: "", message: "", consent: false });
  const [honeypot, setHoneypot] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 1600);
    return () => clearTimeout(t);
  }, []);

  const set = (key) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((p) => ({ ...p, [key]: v }));
    setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!values.name.trim()) return setError("Please tell us your name.");
    if (!isEmail(values.email)) return setError("Enter a valid email address.");
    if (!values.message.trim()) return setError("Please write a short message.");
    if (!values.consent) return setError("Please consent so we can reply to you.");
    setStatus("sending");
    const fd = new FormData();
    fd.append("name", values.name);
    fd.append("email", values.email);
    fd.append("message", values.message);
    fd.append("consent", values.consent ? "yes" : "");
    fd.append("company-website", honeypot);
    const res = await submitForm("chat-message", fd);
    setStatus(res.ok ? "success" : "error");
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-[75] w-[calc(100vw-2rem)] max-w-sm origin-bottom-right overflow-hidden border border-gig-steel/20 bg-white shadow-[0_30px_70px_rgba(10,12,15,0.35)]"
            role="dialog"
            aria-label="Chat with Global Industry Group"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between bg-gig-black px-5 py-4">
              <div>
                <p className="font-condensed text-lg font-bold uppercase tracking-wide text-white">
                  Global Industry Group
                </p>
                <p className="flex items-center gap-1.5 text-xs text-white/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" aria-hidden="true" />
                  We typically reply within one business day
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close" className="text-white/70 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5">
              {status === "success" ? (
                <div className="py-6 text-center" data-testid="chat-success" role="status">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gig-red/10">
                    <Send className="h-5 w-5 text-gig-red" aria-hidden="true" />
                  </span>
                  <p className="mt-4 font-condensed text-xl font-bold uppercase text-gig-black">Message Sent</p>
                  <p className="mt-1 text-sm text-gig-steel">
                    Thanks — a member of the Global Industry Group team will be in touch shortly.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gig-steel">How can we help you today?</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link to="/contact" data-testid="chat-chip-quote" className="border border-gig-steel/25 px-3 py-1.5 font-condensed text-xs font-semibold uppercase tracking-[0.1em] text-gig-black transition-colors hover:border-gig-red hover:text-gig-red">
                      Request a Quote
                    </Link>
                    <a href={SITE.phoneHref} data-testid="chat-chip-call" className="inline-flex items-center gap-1.5 border border-gig-steel/25 px-3 py-1.5 font-condensed text-xs font-semibold uppercase tracking-[0.1em] text-gig-black transition-colors hover:border-gig-red hover:text-gig-red">
                      <Phone className="h-3 w-3" aria-hidden="true" /> {SITE.phone}
                    </a>
                    <Link to="/careers" data-testid="chat-chip-careers" className="inline-flex items-center gap-1.5 border border-gig-steel/25 px-3 py-1.5 font-condensed text-xs font-semibold uppercase tracking-[0.1em] text-gig-black transition-colors hover:border-gig-red hover:text-gig-red">
                      <Briefcase className="h-3 w-3" aria-hidden="true" /> Careers
                    </Link>
                  </div>
                  <form onSubmit={onSubmit} noValidate className="mt-4 space-y-3" data-testid="chat-form">
                    <div>
                      <label htmlFor="chat-name" className="sr-only">Your name</label>
                      <input id="chat-name" data-testid="chat-name" className="field-input !py-2.5 text-sm" placeholder="Your name *" value={values.name} onChange={set("name")} />
                    </div>
                    <div>
                      <label htmlFor="chat-email" className="sr-only">Your email</label>
                      <input id="chat-email" type="email" data-testid="chat-email" className="field-input !py-2.5 text-sm" placeholder="Your email *" value={values.email} onChange={set("email")} />
                    </div>
                    <div>
                      <label htmlFor="chat-message" className="sr-only">Your message</label>
                      <textarea id="chat-message" rows={3} data-testid="chat-message" className="field-input !py-2.5 text-sm" placeholder="Tell us about your project… *" value={values.message} onChange={set("message")} />
                    </div>
                    <div className="hp-field" aria-hidden="true">
                      <label htmlFor="chat-website">Company Website</label>
                      <input id="chat-website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                    </div>
                    <label className="flex items-start gap-2 text-xs text-gig-steel">
                      <input type="checkbox" className="mt-0.5 accent-gig-red" checked={values.consent} onChange={set("consent")} data-testid="chat-consent" />
                      I consent to being contacted about this message.
                    </label>
                    {error && <p className="text-xs font-medium text-gig-red" role="alert" data-testid="chat-error">{error}</p>}
                    {status === "error" && (
                      <p className="text-xs font-medium text-gig-red" role="alert">
                        Something went wrong — please call {SITE.phone} or email {SITE.email}.
                      </p>
                    )}
                    <button type="submit" className="btn-primary w-full !py-2.5 !text-sm" disabled={status === "sending"} data-testid="chat-submit">
                      {status === "sending" ? "Sending…" : "Send Message"}
                      <ArrowRight className="btn-arrow h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {show && (
          <motion.button
            type="button"
            initial={{ opacity: 0, scale: 0.5, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close chat" : "Chat with us"}
            aria-expanded={open}
            data-testid="chat-button"
            className="chat-pulse fixed bottom-5 right-5 z-[75] flex h-14 w-14 items-center justify-center rounded-full bg-gig-red text-white shadow-[0_12px_30px_rgba(229,41,33,0.45)]"
          >
            {open ? <X className="h-6 w-6" aria-hidden="true" /> : <MessageCircle className="h-6 w-6" aria-hidden="true" />}
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
